import React, { useState, useRef } from "react";
import styles from "./addmultiple.module.scss";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase/firebase";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { handlePostRequest } from "../../../services/PostTemplate";

function AddMultipleDialog({onclose}) {
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [showCamera, setShowCamera] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadedUrls, setUploadedUrls] = useState([]);
    const [processing, setProcessing] = useState(false);
    const dispatch = useDispatch();

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [stream, setStream] = useState(null);

    // Add images from file input
    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const newFiles = [...files, ...selectedFiles];
        setFiles(newFiles);
        setPreviews(newFiles.map((file) => URL.createObjectURL(file)));
    };

    // Open camera modal
    const openCamera = async () => {
        setShowCamera(true);
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
            setStream(mediaStream);
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                    videoRef.current.play();
                }
            }, 100);
        } catch (err) {
            toast.error("Unable to access camera.");
            setShowCamera(false);
        }
    };

    // Close camera modal
    const closeCamera = () => {
        setShowCamera(false);
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            setStream(null);
        }
    };

    // Capture photo and add to files/previews
    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && canvas) {
            const ctx = canvas.getContext("2d");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0);
            canvas.toBlob((blob) => {
                const file = new File([blob], `captured-${Date.now()}.png`, { type: "image/png" });
                const url = URL.createObjectURL(blob);
                setFiles((prev) => [...prev, file]);
                setPreviews((prev) => [...prev, url]);
                closeCamera();
            }, "image/png");
        }
    };

    // Remove a file/preview by index
    const handleDelete = (idx) => {
        setFiles(files.filter((_, i) => i !== idx));
        setPreviews(previews.filter((_, i) => i !== idx));
    };

    // Bulk upload to Firebase
    const handleBulkUpload = async () => {
        if (files.length === 0) {
            toast.error("No images to upload!");
            return;
        }
        setUploading(true);
        try {
            const promises = files.map((file) => {
                const storageRef = ref(storage, `packages/${Date.now()}-${file.name}`);
                return uploadBytes(storageRef, file).then(() => getDownloadURL(storageRef));
            });
            const urls = await Promise.all(promises);
            setUploadedUrls(urls);
            toast.success("All images uploaded!");
        } catch (err) {
            toast.error("Bulk upload failed!");
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    // Send all Firebase URLs to backend for package processing
    const handleGeneratePackages = async () => {
        if (uploadedUrls.length === 0) {
            toast.error("Please upload images first!");
            return;
        }
        setProcessing(true);
        try {
            const data = {
                imageUrls: uploadedUrls,
            };
            const res = await dispatch(handlePostRequest(data, "/ocr-packages/multiple", true, true));
            if (res !== "error") {
                toast.success("Packages created successfully!");
                setFiles([]);
                setPreviews([]);
                setUploadedUrls([]);
                onclose();
            }
            else{
                toast.error("Error creating packages!");
            }
            console.log(uploadedUrls);
        } catch (err) {
            toast.error("Error creating packages!");
            console.error(err);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className={styles.container}>
            <h2>Add Multiple Packages</h2>

            <input type="file" accept="image/*" multiple onChange={handleFileChange} />
            <button type="button" className={styles.cameraBtn} onClick={openCamera}>
                Take Photo
            </button>

            <div className={styles.previewsWrapper}>
                {previews.map((url, idx) => (
                    <div key={idx} className={styles.previewBox}>
                        <img src={url} alt={`Preview ${idx + 1}`} />
                        <button className={styles.deleteBtn} onClick={() => handleDelete(idx)}>
                            Delete
                        </button>
                    </div>
                ))}
            </div>

            <button onClick={handleBulkUpload} disabled={files.length === 0 || uploading || uploadedUrls.length > 0} className={styles.uploadBtn}>
                {uploading ? "Uploading..." : "Upload All Images"}
            </button>

            <button onClick={handleGeneratePackages} disabled={uploadedUrls.length === 0 || processing} className={styles.createBtn}>
                {processing ? "Creating..." : "Generate Packages"}
            </button>

            {/* Camera Modal */}
            {showCamera && (
                <div className={styles.cameraModal}>
                    <video ref={videoRef} autoPlay playsInline className={styles.videoPreview} />
                    <div className={styles.cameraActions}>
                        <button onClick={capturePhoto} className={styles.captureBtn}>
                            Capture Photo
                        </button>
                        <button onClick={closeCamera} className={styles.closeBtn}>
                            Close
                        </button>
                    </div>
                    <canvas ref={canvasRef} style={{ display: "none" }} />
                </div>
            )}
        </div>
    );
}

export default AddMultipleDialog;
